/**
 * useHomeQuery - Estatísticas da HomeScreen via React Query (cache persistido).
 *
 * Encapsula a lógica antiga de `loadStats` da HomeScreen: tenta o Supabase
 * quando autenticado e cai para dados locais em caso de erro / não autenticado.
 * Substitui o cache manual `@home_stats_cache_v1`.
 *
 * Shape retornado: { repetitionCount, gamificationStats, weekStudyDays: number[] }
 * (weekStudyDays é array para serializar no cache; a tela converte para Set.)
 */

import { useQuery } from '@tanstack/react-query';
import { getRepetitionStats, loadAnswerLog } from '../../services/storageService';
import { getGamificationStats } from '../../services/gamificationService';
import { getGamificationStatsFromDB, getWeeklyStatsFromDB } from '../../services/databaseService';

export const homeKeys = {
    all: ['home'],
    // userId na chave isola o cache persistido entre contas no mesmo aparelho.
    stats: (userId, scope) => [...homeKeys.all, 'stats', userId ?? 'anon', scope],
};

// Rede de segurança: requisição de rede sem resposta deixaria o queryFn pendurado
// (spinner infinito). Corre contra um timeout que rejeita → cai no fallback local.
const HOME_DB_TIMEOUT_MS = 8000;
const withTimeout = (promise, ms) =>
    Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('home-db-timeout')), ms)),
    ]);

const computeWeekStudyDaysFromLog = (answerLog = []) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Segunda-feira
    startOfWeek.setHours(0, 0, 0, 0);

    const days = [];
    answerLog.forEach((entry) => {
        const entryDate = new Date(entry.timestamp);
        if (entryDate >= startOfWeek) {
            const day = entryDate.getDay(); // 0=Dom, 1=Seg, ...
            if (!days.includes(day)) days.push(day);
        }
    });
    return days;
};

const fetchLocalStats = async () => {
    const [repStats, gamification, answerLog] = await Promise.all([
        getRepetitionStats(),
        getGamificationStats(),
        loadAnswerLog(),
    ]);

    return {
        repetitionCount: repStats.inRepetition,
        gamificationStats: gamification,
        weekStudyDays: computeWeekStudyDaysFromLog(answerLog),
    };
};

const fetchHomeStats = async (userId, isAuthenticated) => {
    if (isAuthenticated && userId) {
        try {
            // userId da sessão → as funções DB não chamam getUser (rede).
            const [repStats, gamificationDB, weeklyDB] = await withTimeout(
                Promise.all([
                    getRepetitionStats(),
                    getGamificationStatsFromDB(userId),
                    getWeeklyStatsFromDB(userId),
                ]),
                HOME_DB_TIMEOUT_MS,
            );

            return {
                repetitionCount: repStats.inRepetition,
                gamificationStats: gamificationDB || null,
                weekStudyDays: weeklyDB?.studyDays || [],
            };
        } catch (error) {
            // Fallback para dados locais (mesmo comportamento do loadStats original)
            return fetchLocalStats();
        }
    }

    return fetchLocalStats();
};

/**
 * Hook das estatísticas da Home. Funciona autenticado (DB) e local.
 */
export const useHomeStats = (userId, isAuthenticated, enabled = true) => {
    return useQuery({
        queryKey: homeKeys.stats(userId, isAuthenticated ? 'auth' : 'local'),
        queryFn: () => fetchHomeStats(userId, isAuthenticated),
        staleTime: 2 * 60 * 1000,
        enabled,
    });
};
